// Shared with the localize-server-time controller so a saved time reads exactly
// like every other local time in the application.
import { formatCalendarTime, parseServerTime } from '../writingio/local_time'

const labels = { saved: 'Saved', saving: 'Saving…', unsaved: 'Unsaved changes', error: 'Needs attention', loading: 'Loading document…' }
const saveViews = {
  saved: { title: 'All changes saved', description: 'Your document is up to date.', icon: 'cloud-check' },
  unsaved: { title: 'Save your latest changes', description: 'Save this document before you close it.', action: 'Save changes', icon: 'cloud-arrow-up' },
  saving: { title: 'Saving your changes…', description: 'Keep this tab open until saving finishes.', action: 'Saving…', icon: 'cloud-arrow-up' },
  error: { title: 'Some changes couldn’t be saved', description: 'Your latest changes are still open here. Keep this tab open while you retry.', action: 'Try again', icon: 'triangle-exclamation' },
  loading: { title: 'Document still loading', description: 'Wait for the whole document to load, or try loading it again.', icon: 'cloud-arrow-down' },
}

// `header` is the editor's save-status cluster. `options.savedAt` is the
// server-persisted last save time for this document and `options.autosave` is a
// getter for the document scheduler's autosave policy, so both come from
// sources the editor already has instead of a copy kept here.
export function buildDeliveryUI(header, options = {}) {
  const trigger = document.createElement('button')
  trigger.type = 'button'
  trigger.className = 'document-save-summary'
  trigger.dataset.saveSummary = ''
  trigger.setAttribute('data-turbo-temporary', '')
  trigger.setAttribute('aria-live', 'polite')
  trigger.setAttribute('aria-haspopup', 'dialog')
  trigger.textContent = 'Checking save status…'
  header?.prepend(trigger)
  const dialog = document.createElement('dialog')
  dialog.className = 'document-delivery-dialog'
  dialog.setAttribute('data-turbo-temporary', '')
  dialog.setAttribute('aria-labelledby', 'document-delivery-title')
  dialog.innerHTML = `
    <div class="document-delivery-heading">
      <span class="delivery-heading-icon" aria-hidden="true"><span class="far fa-cloud-check"></span></span>
      <h2 id="document-delivery-title" tabindex="-1" autofocus>Save document</h2>
      <button type="button" data-delivery-close aria-label="Close"><span class="fal fa-circle-xmark" aria-hidden="true"></span></button>
    </div>
    <div data-save-view>
      <p data-save-description role="status"></p>
      <dl class="delivery-save-meta">
        <div class="delivery-meta-row"><dt>Last saved</dt><dd><time data-save-time></time></dd></div>
        <div class="delivery-meta-row"><dt>Autosave</dt><dd data-save-autosave></dd></div>
      </dl>
      <ul data-save-details aria-live="polite" hidden></ul>
      <p data-save-loading hidden>The rest of this document still needs to load before it is ready to close or export.</p>
      <div class="delivery-actions">
        <button type="button" class="delivery-primary" data-save-all><span class="far fa-check" aria-hidden="true"></span><span data-save-action-label>Save changes</span></button>
        <button type="button" class="delivery-secondary" data-retry-loading hidden><span class="far fa-rotate-right" aria-hidden="true"></span> Retry loading</button>
      </div>
    </div>
    <div data-export-view hidden>
      <p class="delivery-intro">Save your latest changes, then prepare a file to share.</p>
      <fieldset class="document-export-formats"><legend>File format</legend>
        <div class="delivery-format-grid">
          <label><input type="radio" name="document-export-format" value="word" checked><span class="far fa-file-word delivery-file-icon" aria-hidden="true"></span><span class="delivery-format-name">Word<small>Editable .docx</small></span></label>
          <label><input type="radio" name="document-export-format" value="pdf"><span class="far fa-file-pdf delivery-file-icon" aria-hidden="true"></span><span class="delivery-format-name">PDF<small>Print / save as PDF</small></span></label>
          <label><input type="radio" name="document-export-format" value="markdown"><span class="far fa-file-code delivery-file-icon" aria-hidden="true"></span><span class="delivery-format-name">Markdown<small>Editable .md, every section</small></span></label>
        </div>
      </fieldset>
      <div class="delivery-export-status"><span class="far fa-circle-info" aria-hidden="true"></span><p data-export-message role="status" aria-live="polite">Save and check your document before exporting.</p></div>
      <div class="delivery-actions">
        <button type="button" class="delivery-primary" data-export-prepare><span class="far fa-file-arrow-down" aria-hidden="true"></span><span data-delivery-label>Prepare Word</span></button>
        <button type="button" class="delivery-primary" data-export-download hidden><span data-delivery-action-icon aria-hidden="true"><span class="far fa-download"></span></span><span data-delivery-label>Download Word</span></button>
        <button type="button" class="delivery-secondary" data-export-saves><span class="far fa-cloud-check" aria-hidden="true"></span> Save status</button>
      </div>
    </div>`
  if (!header) {
    dialog.querySelector('.delivery-intro').textContent = 'Choose a format for the saved version of this document.'
    dialog.querySelector('[data-export-saves]').hidden = true
  }
  document.body.append(dialog)
  let opener, currentState
  let currentView = 'save'
  // A panel opened while work was pending dismisses itself once the save lands.
  // A panel opened to read an already-saved document is a status view and stays
  // until the writer dismisses it.
  let autoClose = true
  const heading = () => {
    const view = currentView === 'export' ? { title: 'Export document', icon: 'file-export' } : saveViews[currentState?.status] || saveViews.loading
    dialog.querySelector('#document-delivery-title').textContent = view.title
    dialog.querySelector('.delivery-heading-icon').innerHTML = `<span class="far fa-${view.icon}"></span>`
  }
  const autosaveOn = () => typeof options.autosave === 'function' ? Boolean(options.autosave()) : Boolean(options.autosave)
  const sectionAutosaveOn = () => typeof options.sectionAutosave === 'function'
    ? Boolean(options.sectionAutosave())
    : Boolean(options.sectionAutosave)
  const showSavedTime = () => {
    const time = dialog.querySelector('[data-save-time]')
    const value = time.dataset.serverTimeValue
    const display = value ? formatCalendarTime(value) : ''
    time.textContent = display || 'Last save time unavailable'
    if (value) time.setAttribute('datetime', value)
    else time.removeAttribute('datetime')
  }
  const renderMeta = () => {
    showSavedTime()
    const documentPolicy = autosaveOn()
      ? 'On — document edits save automatically.'
      : 'Off — document edits require Save.'
    const sectionPolicy = sectionAutosaveOn() ? ' Section edits save automatically.' : ''
    dialog.querySelector('[data-save-autosave]').textContent = `${documentPolicy}${sectionPolicy}`
  }
  const isVisible = element => {
    if (!element?.isConnected || element.hidden || element.disabled) return false
    if (element.getClientRects && element.getClientRects().length === 0) return false
    const style = window.getComputedStyle?.(element)
    return !style || (style.visibility !== 'hidden' && style.display !== 'none')
  }
  const latestServerTime = (current, candidate) => {
    const candidateTime = parseServerTime(candidate)
    if (!candidateTime) return current || ''
    const currentTime = parseServerTime(current)
    return !currentTime || candidateTime.valueOf() >= currentTime.valueOf() ? candidate : current
  }
  const setSavedAt = value => {
    const time = dialog.querySelector('[data-save-time]')
    const current = time.dataset.serverTimeValue || ''
    const latest = latestServerTime(current, value)
    time.dataset.serverTimeValue = latest
    showSavedTime()
    if (latest !== current) options.onSavedAt?.(latest)
    return latest
  }
  const resolveOpener = () => typeof opener === 'function' ? opener() : opener
  const fallbackOpener = () => typeof options.fallbackOpener === 'function' ? options.fallbackOpener() : null
  const focusReturnTarget = () => {
    const origin = resolveOpener()
    if (isVisible(origin)) return origin
    const fallback = fallbackOpener()
    return isVisible(fallback) ? fallback : null
  }
  const restoreFocus = () => {
    const focusTarget = () => {
      if (dialog.open) return false
      const target = focusReturnTarget()
      if (!target) return false
      target.focus({ preventScroll: true })
      return document.activeElement === target
    }
    // Chromium may restore the focus that preceded showModal after the close
    // event. Re-apply the current visible target for a few animation frames so
    // native dialog focus management cannot strand focus on the old opener or
    // the dialog heading while a responsive header is settling.
    const retry = remaining => {
      if (focusTarget() || remaining <= 0) return
      setTimeout(() => retry(remaining - 1), 16)
    }
    focusTarget()
    setTimeout(() => retry(4), 0)
  }
  const syncExpanded = () => {
    const target = resolveOpener()
    if (target?.hasAttribute?.('aria-haspopup') || target?.hasAttribute?.('aria-expanded')) {
      target.setAttribute('aria-expanded', String(dialog.open))
    }
  }
  renderMeta()
  setSavedAt(options.savedAt)
  const close = () => {
    if (!dialog.open) return
    dialog.close()
    // Native dialog close events can be queued after `open` becomes false.
    // Restore immediately as well as from the event so callers never observe
    // a closed panel with focus stranded on BODY.
    restoreFocus()
    syncExpanded()
  }
  dialog.querySelector('[data-delivery-close]').addEventListener('click', close)
  dialog.addEventListener('close', () => {
    // Responsive controls may change while the dialog is open.
    restoreFocus()
    syncExpanded()
  })
  dialog.addEventListener('click', event => { if (event.target === dialog) close() })
  return {
    trigger, dialog,
    // `status: true` is for callers that own an explicit status control: the
    // desktop summary stays quiet once everything is saved, while the compact
    // header indicator always opens the status it is showing.
    open(view, origin = document.activeElement, { status = false } = {}) {
      if (view === 'save' && currentState?.status === 'saved' && !status) return
      if (!dialog.open) opener = origin
      autoClose = currentState?.status !== 'saved'
      currentView = view
      dialog.dataset.view = view
      heading()
      dialog.querySelector('[data-save-view]').hidden = view !== 'save'
      dialog.querySelector('[data-export-view]').hidden = view !== 'export'
      renderMeta()
      if (!dialog.open) dialog.showModal()
      dialog.querySelector('#document-delivery-title').focus({ preventScroll: true })
      syncExpanded()
    },
    openStatus(origin = document.activeElement) { this.open('save', origin, { status: true }) },
    setSavedAt,
    render(state) {
      currentState = state
      heading()
      renderMeta()
      const view = saveViews[state.status]
      const saved = state.status === 'saved'
      const focusedAction = dialog.contains(document.activeElement) ? document.activeElement : null
      if (trigger.textContent !== labels[state.status]) trigger.textContent = labels[state.status]
      trigger.dataset.state = state.status
      trigger.setAttribute('aria-label', saved ? 'All changes saved' : `${labels[state.status]}. View document save status`)
      trigger.setAttribute('aria-disabled', String(saved))
      trigger.tabIndex = saved ? -1 : 0
      if (saved) trigger.removeAttribute('aria-haspopup')
      else trigger.setAttribute('aria-haspopup', 'dialog')
      header?.closest('.editor-header')?.querySelectorAll('[data-document-save-details]').forEach(button => { button.hidden = saved })
      dialog.querySelector('[data-export-saves]').hidden = saved
      dialog.querySelector('[data-save-description]').textContent = view.description
      dialog.querySelector('[data-retry-loading]').hidden = state.loaded >= state.total
      dialog.querySelector('[data-save-loading]').hidden = state.loaded >= state.total || state.status === 'loading'
      const list = dialog.querySelector('[data-save-details]')
      const failures = state.resources.filter(row => row.status === 'error')
      list.hidden = failures.length === 0
      list.replaceChildren(...failures.map(row => {
        const li = document.createElement('li')
        const name = document.createElement('span');name.textContent = row.label
        const reason = document.createElement('span');reason.textContent = row.errorReason;reason.className = 'save-failure-reason'
        li.append(name, reason)
        return li
      }))
      const save = dialog.querySelector('[data-save-all]')
      save.hidden = !view.action
      save.disabled = state.status === 'saving'
      save.querySelector('[data-save-action-label]').textContent = view.action || 'Save changes'
      save.querySelector('[aria-hidden]').className = `far fa-${state.status === 'error' ? 'rotate-right' : state.status === 'saving' ? 'cloud-arrow-up' : 'check'}`
      if (saved && currentView === 'save' && dialog.open) {
        if (autoClose) close()
        // A status panel stays open; keep focus on something visible once the
        // save action it may have held is hidden.
        else if (focusedAction?.hidden) dialog.querySelector('#document-delivery-title').focus({ preventScroll: true })
      }
      else if (currentView === 'save' && focusedAction?.hidden) {
        const next = save.hidden ? dialog.querySelector('[data-retry-loading]') : save
        next.focus({ preventScroll: true })
      } else if (currentView === 'export' && focusedAction?.hidden) {
        const next = dialog.querySelector('[data-export-prepare]:not([hidden]):not(:disabled), [data-export-download]:not([hidden]):not(:disabled)') || dialog.querySelector('#document-delivery-title')
        next.focus({ preventScroll: true })
      }
    },
    destroy() { dialog.remove();trigger.remove() },
  }
}
